import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { Button, DatePicker, Modal, Select, Table } from "../../../components";
import { formatDateForBackend } from "../../../features/dateFormaters";
import {
  CREATE_PRICE_RATE_URL,
  DELETE_PRICE_RATE_URL,
  GET_PRICE_LISTS_URL,
  GET_PRICE_RATES_URL,
  UPDATE_PRICE_RATE_URL,
} from "../../../features/UrlHelper";

const getSlovakToday = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Bratislava",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const dateParts = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
};

// PostgreSQL TIMESTAMP vracia hodnotu, pri ktorej nechceme robiť UTC posun.
const toLocalDateInput = (value) => {
  if (!value) return getSlovakToday();
  const datePart = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  return datePart || getSlovakToday();
};

const formatDate = (value) => {
  if (!value) return "-";
  const datePart = toLocalDateInput(value);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "-";
  return `${day}.${month}.${year}`;
};

const formatPrice = (value) => `${Number(value).toFixed(2)} €`;

const emptyForm = { kod_cennik: "", kod_menu: "", cena: "", platnost_od: toLocalDateInput() };
const isEditablePrice = (price) => String(price?.platnost_od || "").slice(0, 10) > getSlovakToday();

const PriceRatesSection = () => {
  const [priceLists, setPriceLists] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKodCennik, setFilterKodCennik] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Chyby z formulara sa zobrazuju v modale, chyby z nacitania a mazania nad
  // tabulkou -- pri zavretom modale by inak neboli vidiet vobec.
  const [formError, setFormError] = useState("");
  const [sectionError, setSectionError] = useState("");


  const loadPriceLists = async () => {
    try {
      const response = await axios.get(GET_PRICE_LISTS_URL);
      setPriceLists(response.data);
    } catch (error) {
      setSectionError(error.response?.data?.message || "Cenníky sa nepodarilo načítať.");
    }
  };

  const loadPrices = async (kodCennik) => {
    setLoading(true);
    try {
      const response = await axios.get(GET_PRICE_RATES_URL, {
        params: kodCennik ? { kod_cennik: kodCennik } : {},
      });
      setPrices(response.data);
    } catch (error) {
      setSectionError(error.response?.data?.message || "Ceny sa nepodarilo načítať.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPriceLists();
  }, []);

  useEffect(() => {
    loadPrices(filterKodCennik);
  }, [filterKodCennik]);

  const priceListOptions = useMemo(
    () => priceLists.map((priceList) => ({ value: priceList.kod, label: priceList.kod })),
    [priceLists]
  );

  const openAddModal = () => {
    setEditingPrice(null);
    setForm({ ...emptyForm, kod_cennik: filterKodCennik || "" });
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (price) => {
    if (!isEditablePrice(price)) return;

    setEditingPrice(price);
    setForm({
      kod_cennik: price.kod_cennik || "",
      kod_menu: price.kod_menu || "",
      cena: price.cena ?? "",
      platnost_od: toLocalDateInput(price.platnost_od),
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!isSubmitting) setModalOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (editingPrice && !isEditablePrice(editingPrice)) {
      setModalOpen(false);
      setSectionError("Platnú cenu už nie je možné upravovať, len zmazať.");
      return;
    }

    setIsSubmitting(true);
    setFormError("");
    const payload = {
      kod_cennik: form.kod_cennik,
      kod_menu: form.kod_menu,
      cena: form.cena,
      platnost_od: form.platnost_od,
    };

    try {
      if (editingPrice) {
        await axios.put(UPDATE_PRICE_RATE_URL(editingPrice.id), payload);
      } else {
        await axios.post(CREATE_PRICE_RATE_URL, payload);
      }
      setModalOpen(false);
      await loadPrices(filterKodCennik);
    } catch (error) {
      setFormError(error.response?.data?.message || "Cenu sa nepodarilo uložiť.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deletePrice = async (id) => {
    try {
      await axios.delete(DELETE_PRICE_RATE_URL(id));
      setPrices((current) => current.filter((price) => price.id !== id));
    } catch (error) {
      setSectionError(error.response?.data?.message || "Cenu sa nepodarilo zmazať.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Ceny a sadzby</h2>
        <Button variant="primary" onClick={openAddModal}>
          <Plus size={16} /> Nová cena
        </Button>
      </div>

      {sectionError && <p className="admin__form-error" role="alert">{sectionError}</p>}

      <div className="admin__table-wrapper">
        <Table
          columns={[
            { key: "kod_cennik", label: "Cenník" },
            { key: "kod_menu", label: "Kód menu" },
            { key: "cena", label: "Cena", render: (value) => formatPrice(value) },
            { key: "platnost_od", label: "Platnosť od", render: (value) => formatDate(value) },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  {isEditablePrice(row) && (
                    <button className="admin__icon-btn" onClick={() => openEditModal(row)} title="Upraviť">
                      <Pencil size={16} />
                    </button>
                  )}
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    onClick={() => setDeleteConfirm(row)}
                    title="Zmazať"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={prices}
          loading={loading}
          searchPlaceholder="Hľadať cenu..."
          emptyMessage="Žiadne ceny"
          toolbarContent={
            <Select
              value={filterKodCennik}
              onChange={(val) => setFilterKodCennik(val)}
              options={[{ value: "", label: "Všetky cenníky" }, ...priceListOptions]}
            />
          }
        />
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingPrice ? "Upraviť cenu" : "Nová cena"}
      >
        <form className="admin__form" onSubmit={handleSubmit}>
          <div className="admin__form-field">
            <Select
              label="Cenník"
              options={priceListOptions}
              value={form.kod_cennik}
              onChange={(val) => setForm((current) => ({ ...current, kod_cennik: val }))}
              placeholder="Vyber cenník..."
              required
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label" htmlFor="price-rate-menu-code">Kód menu</label>
            <input
              id="price-rate-menu-code"
              className="admin__form-input"
              value={form.kod_menu}
              maxLength={3}
              onChange={(event) => setForm((current) => ({
                ...current,
                kod_menu: event.target.value.replace(/\s+/g, "").toUpperCase(),
              }))}
              placeholder="napr. A"
              required
            />
          </div>
          <div className="admin__form-field">
            <label className="admin__form-label" htmlFor="price-rate-price">Cena (€)</label>
            <input
              id="price-rate-price"
              type="number"
              step="0.01"
              min="0.01"
              className="admin__form-input"
              value={form.cena}
              onChange={(event) => setForm((current) => ({ ...current, cena: event.target.value }))}
              placeholder="napr. 4.50"
              required
            />
          </div>
          <div className="admin__form-field">
            <DatePicker
              label="Platnosť od"
              value={form.platnost_od}
              onChange={(date) => setForm((current) => ({
                ...current,
                platnost_od: date ? formatDateForBackend(date) : "",
              }))}
            />
          </div>
          {formError && <p className="admin__form-error" role="alert">{formError}</p>}
          <div className="admin__form-actions">
            <Button variant="ghost" type="button" onClick={closeModal} disabled={isSubmitting}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Ukladám..." : "Uložiť"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať túto cenu? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Zrušiť</Button>
          <Button
            variant="primary"
            onClick={() => deletePrice(deleteConfirm.id)}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default PriceRatesSection;
